import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { useTheme } from "@/hooks/use-theme";
import { OnboardingProvider, useOnboarding } from "@/hooks/use-onboarding";
import { TooltipOnboarding } from "@/components/ui/tooltip-onboarding";
import { DebugTourInfo } from "@/components/debug-tour-info";
import AppLayout from "@/components/layout/app-layout";
import SuperadminLayout from "@/components/layout/superadmin-layout";
import { IntercomProvider } from "@/components/intercom-provider";
import { useEffect, lazy, Suspense, type ComponentType } from "react";
import { useOrganizationTheme } from "@/hooks/use-organization-theme";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

// Eager load critical pages
import AuthSimple from "@/pages/auth-simple";
import Dashboard from "@/pages/dashboard";
import NotFound from "@/pages/not-found";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";

// Page-specific skeletons
import { CustomersSkeleton } from "@/pages/customers";
import { AssessmentsSkeleton } from "@/pages/assessments-technical-alignment";
import { QbrsSkeleton } from "@/pages/qbrs";

// Resilient dynamic import for lazy routes.
// "Failed to fetch dynamically imported module" happens when a chunk can't be
// loaded: transient dev-server churn (HMR / dep re-optimization) or, in
// production, a stale chunk hash after a new deploy while a tab stayed open.
// We retry once, then force a single guarded full reload (sessionStorage flag
// prevents reload loops) to pull the fresh module graph; if it still fails the
// error propagates to the route error boundary.
const DYNAMIC_IMPORT_RELOAD_FLAG = "techalign:dynamicImportReloaded";

function getReloadFlag(): boolean {
  try {
    return sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_FLAG) === "1";
  } catch {
    return false;
  }
}

function setReloadFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_FLAG, "1");
    else sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_FLAG);
  } catch {
    // sessionStorage may be unavailable (private mode); fail open.
  }
}

function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      setReloadFlag(false);
      return mod;
    } catch {
      try {
        const mod = await factory();
        setReloadFlag(false);
        return mod;
      } catch (err) {
        if (!getReloadFlag()) {
          setReloadFlag(true);
          window.location.reload();
          // Keep Suspense in its fallback until the reload takes over.
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }
    }
  });
}

// Lazy load all other pages
const Profile = lazyWithRetry(() => import("@/pages/profile"));
const AssessmentsHub = lazyWithRetry(() => import("@/pages/assessments-hub"));
const AssessmentsTechnicalAlignment = lazyWithRetry(() => import("@/pages/assessments-technical-alignment"));
const AssessmentDetail = lazyWithRetry(() => import("@/pages/assessment-detail"));
const AssessmentReview = lazyWithRetry(() => import("@/pages/assessment-review"));
const Customers = lazyWithRetry(() => import("@/pages/customers"));
const CustomerDetail = lazyWithRetry(() => import("@/pages/customer-detail"));
const Qbrs = lazyWithRetry(() => import("@/pages/qbrs"));
const QbrDetail = lazyWithRetry(() => import("@/pages/qbr-detail"));
const Recommendations = lazyWithRetry(() => import("@/pages/recommendations"));
const Templates = lazyWithRetry(() => import("@/pages/templates"));
const Help = lazyWithRetry(() => import("@/pages/help"));
const Integrations = lazyWithRetry(() => import("@/pages/integrations"));
const Settings = lazyWithRetry(() => import("@/pages/settings"));
const UserManagement = lazyWithRetry(() => import("@/pages/user-management"));
const Activity = lazyWithRetry(() => import("@/pages/activity"));
const AuditLogsPage = lazyWithRetry(() => import("@/pages/audit-logs").then(m => ({ default: m.AuditLogsPage })));
const QuarterlyReviews = lazyWithRetry(() => import("@/pages/quarterly-reviews"));
const BudgetPlanning = lazyWithRetry(() => import("@/pages/budget-planning"));
const BudgetTemplates = lazyWithRetry(() => import("@/pages/budget-templates"));
const Calendar = lazyWithRetry(() => import("@/pages/calendar"));
const Billing = lazyWithRetry(() => import("@/pages/billing"));
const VerifyEmail = lazyWithRetry(() => import("@/pages/verify-email"));
const AdminDashboard = lazyWithRetry(() => import("@/pages/admin-dashboard"));
const RateLimitMonitoring = lazyWithRetry(() => import("@/pages/rate-limit-monitoring"));
const TechStackStandards = lazyWithRetry(() => import("@/pages/tech-stack-standards"));
const TechStackAudits = lazyWithRetry(() => import("@/pages/tech-stack-audits"));
const ReviewCyclePage = lazyWithRetry(() => import("@/pages/review-cycle-page"));
const StandardChangePage = lazyWithRetry(() => import("@/pages/standard-change"));
const Assets = lazyWithRetry(() => import("@/pages/assets"));
const AssetDetail = lazyWithRetry(() => import("@/pages/asset-detail"));
const ImplementationTracking = lazyWithRetry(() => import("@/pages/implementation-tracking"));
const ReportLibrary = lazyWithRetry(() => import("@/pages/reports/ReportLibrary"));
const ReportView = lazyWithRetry(() => import("@/pages/reports/ReportView"));
const ReportPrint = lazyWithRetry(() => import("@/pages/reports/ReportPrint"));

// Loading fallback component with skeleton
const PageSkeleton = () => (
  <div className="p-4 space-y-3">
    <div className="space-y-2">
      <div className="h-8 w-48 bg-muted rounded animate-pulse"></div>
      <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader className="pb-2">
            <div className="h-4 bg-muted rounded w-3/4"></div>
          </CardHeader>
          <CardContent>
            <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-muted rounded w-2/3"></div>
          </CardContent>
        </Card>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {[...Array(2)].map((_, i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-5 bg-muted rounded w-1/3"></div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
            <div className="h-4 bg-muted rounded w-4/6"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
);

const PageLoader = () => <PageSkeleton />;

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, isLoading } = useTheme();
  useOrganizationTheme(); // Apply organization theme

  return <>{children}</>;
}

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();

  // SECURITY: Simplified security - only apply cache headers without aggressive checks
  useEffect(() => {
    // Only add cache-control headers without aggressive validation
    const meta = document.querySelector('meta[http-equiv="Cache-Control"]');
    if (!meta && isAuthenticated) {
      const cacheControl = document.createElement("meta");
      cacheControl.httpEquiv = "Cache-Control";
      cacheControl.content = "no-cache, no-store, must-revalidate";
      document.head.appendChild(cacheControl);
    }
  }, [isAuthenticated]);

  // Check for password change requirement
  useEffect(() => {
    if (isAuthenticated && user) {
      const urlParams = new URLSearchParams(window.location.search);
      const passwordChangeRequired =
        urlParams.get("passwordChange") === "required";

      if (passwordChangeRequired && window.location.pathname !== "/profile") {
        // Force navigation to profile page for password change
        window.location.href = "/profile?passwordChange=required";
      }
    }
  }, [isAuthenticated, user]);

  if (isLoading) {
    return <PageSkeleton />;
  }

  return (
    <Switch>
      {/* Verification route should be accessible regardless of auth status */}
      <Route path="/verify-email/:userId?">
        <Suspense fallback={<PageLoader />}>
          <VerifyEmail />
        </Suspense>
      </Route>

      {!isAuthenticated ? (
        <>
          <Route path="/auth" component={AuthSimple} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/" component={AuthSimple} />
          <Route component={AuthSimple} />
        </>
      ) : (
        <Switch>
          {/* Superadmin dashboard - completely separate from organization platform */}
          <Route path="/admin/:tab">
            {user?.isSuperadmin ? (
              <SuperadminLayout>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              </SuperadminLayout>
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                  <h1 className="text-lg font-bold text-destructive mb-2">
                    Access Denied
                  </h1>
                  <p className="text-muted-foreground">
                    Superadmin access required
                  </p>
                </div>
              </div>
            )}
          </Route>
          <Route path="/admin">
            {user?.isSuperadmin ? (
              <SuperadminLayout>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              </SuperadminLayout>
            ) : (
              <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center">
                  <h1 className="text-lg font-bold text-destructive mb-2">
                    Access Denied
                  </h1>
                  <p className="text-muted-foreground">
                    Superadmin access required
                  </p>
                </div>
              </div>
            )}
          </Route>

          {/* Bare print route — rendered WITHOUT the app chrome (no sidebar/nav)
              so the headless browser prints just the report document. Must be
              registered before the layout-wrapped catch-all Route below, and
              the more-specific /print path must come before /reports/:id. */}
          <Route path="/reports/:id/print">
            <Suspense fallback={<PageLoader />}>
              <ReportPrint />
            </Suspense>
          </Route>

          {/* Regular platform routes with full layout */}
          <Route>
            <OnboardingProvider
              userId={user?.id}
              userRoles={user?.role ? [user.role] : []}
              autoStart={true}
            >
              <OnboardingWrapper>
                <AppLayout>
                  <Switch>
                    <Route path="/" component={Dashboard} />
                    <Route path="/assessments/technical-alignment">
                      <Suspense fallback={<AssessmentsSkeleton />}>
                        <AssessmentsTechnicalAlignment />
                      </Suspense>
                    </Route>
                    <Route path="/assessments/:id/review">
                      <Suspense fallback={<PageLoader />}>
                        <AssessmentReview />
                      </Suspense>
                    </Route>
                    <Route path="/assessments/:id">
                      <Suspense fallback={<PageLoader />}>
                        <AssessmentDetail />
                      </Suspense>
                    </Route>
                    <Route path="/assessments">
                      <Suspense fallback={<AssessmentsSkeleton />}>
                        <AssessmentsHub />
                      </Suspense>
                    </Route>
                    <Route path="/customers">
                      <Suspense fallback={<CustomersSkeleton />}>
                        <Customers />
                      </Suspense>
                    </Route>
                    <Route path="/customers/:id/:tab">
                      <Suspense fallback={<CustomersSkeleton />}>
                        <CustomerDetail />
                      </Suspense>
                    </Route>
                    <Route path="/customers/:id">
                      <Suspense fallback={<CustomersSkeleton />}>
                        <CustomerDetail />
                      </Suspense>
                    </Route>
                    <Route path="/qbrs">
                      <Suspense fallback={<QbrsSkeleton />}>
                        <Qbrs />
                      </Suspense>
                    </Route>
                    <Route path="/qbrs/:id">
                      <Suspense fallback={<QbrsSkeleton />}>
                        <QbrDetail />
                      </Suspense>
                    </Route>
                    <Route path="/recommendations">
                      <Suspense fallback={<PageLoader />}>
                        <Recommendations />
                      </Suspense>
                    </Route>
                    <Route path="/review-schedule">
                      <Suspense fallback={<PageLoader />}>
                        <QuarterlyReviews />
                      </Suspense>
                    </Route>
                    {/* Legacy path kept so existing links/bookmarks keep working. */}
                    <Route path="/quarterly-reviews">
                      <Suspense fallback={<PageLoader />}>
                        <QuarterlyReviews />
                      </Suspense>
                    </Route>
                    <Route path="/quarterly-reviews/:id">
                      <Suspense fallback={<PageLoader />}>
                        <ReviewCyclePage />
                      </Suspense>
                    </Route>
                    <Route path="/budget-planning">
                      <Suspense fallback={<PageLoader />}>
                        <BudgetPlanning />
                      </Suspense>
                    </Route>
                    <Route path="/implementation-tracking">
                      <Suspense fallback={<PageLoader />}>
                        <ImplementationTracking />
                      </Suspense>
                    </Route>
                    <Route path="/budget-templates">
                      <Suspense fallback={<PageLoader />}>
                        <BudgetTemplates />
                      </Suspense>
                    </Route>
                    <Route path="/calendar">
                      <Suspense fallback={<PageLoader />}>
                        <Calendar />
                      </Suspense>
                    </Route>
                    <Route path="/templates">
                      <Suspense fallback={<PageLoader />}>
                        <Templates />
                      </Suspense>
                    </Route>
                    <Route path="/help">
                      <Suspense fallback={<PageLoader />}>
                        <Help />
                      </Suspense>
                    </Route>
                    <Route path="/integrations/:tab">
                      <Suspense fallback={<PageLoader />}>
                        <Integrations />
                      </Suspense>
                    </Route>
                    <Route path="/integrations">
                      <Suspense fallback={<PageLoader />}>
                        <Integrations />
                      </Suspense>
                    </Route>
                    <Route path="/profile">
                      <Suspense fallback={<PageLoader />}>
                        <Profile />
                      </Suspense>
                    </Route>
                    <Route path="/settings">
                      <Suspense fallback={<PageLoader />}>
                        <Settings />
                      </Suspense>
                    </Route>
                    <Route path="/users">
                      <Suspense fallback={<PageLoader />}>
                        <UserManagement />
                      </Suspense>
                    </Route>
                    <Route path="/activity">
                      <Suspense fallback={<PageLoader />}>
                        <Activity />
                      </Suspense>
                    </Route>
                    <Route path="/audit-logs">
                      <Suspense fallback={<PageLoader />}>
                        <AuditLogsPage />
                      </Suspense>
                    </Route>
                    <Route path="/billing">
                      <Suspense fallback={<PageLoader />}>
                        <Billing />
                      </Suspense>
                    </Route>
                    <Route path="/rate-limit-monitoring">
                      <Suspense fallback={<PageLoader />}>
                        <RateLimitMonitoring />
                      </Suspense>
                    </Route>
                    <Route path="/tech-stack-standards/:id/changes">
                      <Suspense fallback={<PageLoader />}>
                        <StandardChangePage />
                      </Suspense>
                    </Route>
                    <Route path="/tech-stack-standards">
                      <Suspense fallback={<PageLoader />}>
                        <TechStackStandards />
                      </Suspense>
                    </Route>
                    <Route path="/tech-stack-audits">
                      <Suspense fallback={<PageLoader />}>
                        <TechStackAudits />
                      </Suspense>
                    </Route>
                    <Route path="/review-cycles/:id/unified">
                      <Suspense fallback={<PageLoader />}>
                        <ReviewCyclePage />
                      </Suspense>
                    </Route>
                    <Route path="/reports/:id">
                      <Suspense fallback={<PageLoader />}>
                        <ReportView />
                      </Suspense>
                    </Route>
                    <Route path="/reports">
                      <Suspense fallback={<PageLoader />}>
                        <ReportLibrary />
                      </Suspense>
                    </Route>
                    <Route path="/budget-reports">
                      <Redirect to="/reports" />
                    </Route>
                    <Route path="/assets/:id/:tab">
                      <Suspense fallback={<PageLoader />}>
                        <AssetDetail />
                      </Suspense>
                    </Route>
                    <Route path="/assets/:id">
                      <Suspense fallback={<PageLoader />}>
                        <AssetDetail />
                      </Suspense>
                    </Route>
                    <Route path="/assets">
                      <Suspense fallback={<PageLoader />}>
                        <Assets />
                      </Suspense>
                    </Route>
                    <Route component={NotFound} />
                  </Switch>
                </AppLayout>
              </OnboardingWrapper>
            </OnboardingProvider>
          </Route>
        </Switch>
      )}
      <Route component={NotFound} />
    </Switch>
  );
}

function OnboardingWrapper({ children }: { children: React.ReactNode }) {
  const {
    currentStep,
    currentTour,
    currentStepIndex,
    totalSteps,
    isOnboardingActive,
    nextStep,
    previousStep,
    skipTour,
    completeTour,
    dismissOnboarding,
  } = useOnboarding();

  return (
    <>
      <DebugTourInfo />
      <TooltipOnboarding
        isVisible={isOnboardingActive}
        currentStep={currentStep}
        currentTour={currentTour}
        stepIndex={currentStepIndex}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipTour}
        onComplete={completeTour}
        onDismiss={dismissOnboarding}
      >
        {children}
      </TooltipOnboarding>
    </>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();

  // Force-logout idle users once their session timeout elapses.
  useIdleTimeout();

  return (
    <TooltipProvider>
      <ThemeProvider>
        <Toaster />
        <Router />
        {/* Initialize Intercom messenger when authenticated */}
        {isAuthenticated && <IntercomProvider />}
      </ThemeProvider>
    </TooltipProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
